/**
 * PathwayView Metabolic Dashboard fixtures (Sprint 4.1 REDO).
 *
 * Source-of-truth: the live prototype at
 * https://ai-native-prototype.vercel.app/care-pathway-studio.html
 * PathwayView tab. The prototype is a card-based dashboard with three
 * status rings (Heart / Metabolic / Inflammation), a presenter action
 * bar, and a PROM Collection sub-tab carrying the four-step admin
 * schedule. This module captures the synthetic content the dashboard
 * renders.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value is synthetic.
 * Per HC5: zero em / en dashes (hyphens only).
 */
import type { PatientFixture } from '../../data/synthetic-patients/types';

/**
 * Closed enum of status colors for the dashboard rings. Maps onto Signal
 * DS tokens at render time (see `MetabolicDashboard.tsx`):
 *   - `attention` -> warning-strong palette (red)
 *   - `improving` -> signal-primary palette (blue)
 *   - `optimal`   -> status-active palette (green)
 */
export type MetabolicStatus = 'attention' | 'improving' | 'optimal';

export interface MetabolicMetric {
  /** Title shown above the ring (Heart / Metabolic / Inflammation). */
  title: string;
  /** Status ring semantic color. */
  status: MetabolicStatus;
  /** Status label text under the ring. */
  statusLabel: string;
  /** One-line metric summary (e.g. `LDL 142 mg/dL`). */
  metricSummary: string;
}

/**
 * The three-column dashboard fixture. Numbers reflect Omar Al-Rashidi's
 * TLP3 chart (HbA1c 7.4 in the patient fixture, ApoB 112). The prototype
 * uses round, illustrative values; this module mirrors the prototype.
 */
export const METABOLIC_DASHBOARD: readonly MetabolicMetric[] = [
  {
    title: 'Heart',
    status: 'attention',
    statusLabel: 'Needs attention',
    metricSummary: 'LDL 142 mg/dL',
  },
  {
    title: 'Metabolic',
    status: 'improving',
    statusLabel: 'Improving',
    metricSummary: 'HbA1c 7.1%',
  },
  {
    title: 'Inflammation',
    status: 'optimal',
    statusLabel: 'Optimal',
    metricSummary: 'hs-CRP 3.4 mg/L',
  },
];

/**
 * Closed enum of PROM Collection step states. Drives the badge color
 * + the disabled state of the schedule row.
 */
export type PromStepStatus = 'complete' | 'scheduled';

export interface PromCollectionStep {
  id: string;
  /** Step ordinal (1-4). */
  ordinal: number;
  status: PromStepStatus;
  /** Status label rendered in the badge. */
  statusLabel: string;
  /** Date label (e.g. `Mar 22`, `Apr 19`). */
  dateLabel: string;
  /** Closed list of instrument codes to render as chips. */
  instruments: readonly string[];
}

/**
 * Four-step PROM admin schedule for TLP1 (Omar's TLP3 enrollment uses
 * the same instrument library; the prototype's PathwayView shows the
 * TLP1 sub-tab as the canonical example). Verbatim instrument set per
 * the master prompt v3.3 instrument catalog.
 */
export const PROM_COLLECTION_STEPS: readonly PromCollectionStep[] = [
  {
    id: 'tlp1-step-1',
    ordinal: 1,
    status: 'complete',
    statusLabel: 'Complete',
    dateLabel: 'Mar 22',
    instruments: ['MSQ', 'PHQ-9', 'SF-12'],
  },
  {
    id: 'tlp1-step-2',
    ordinal: 2,
    status: 'scheduled',
    statusLabel: 'Scheduled',
    dateLabel: 'Apr 19',
    instruments: ['PHQ-9', 'SF-12', 'DQOLY'],
  },
  {
    id: 'tlp1-step-3',
    ordinal: 3,
    status: 'scheduled',
    statusLabel: 'Scheduled',
    dateLabel: 'May 3',
    instruments: ['GSRS', 'IWQOL', 'PHQ-9'],
  },
  {
    id: 'tlp1-step-4',
    ordinal: 4,
    status: 'scheduled',
    statusLabel: 'Scheduled',
    dateLabel: 'Jul 19',
    instruments: ['MSQ', 'PHQ-9', 'SF-12', 'GSRS', 'IWQOL', 'DQOLY'],
  },
];

/**
 * Verbatim patient-journey headline from the prototype's PathwayView.
 * HC5: hyphens only, no em / en dash.
 */
export const PATHWAY_VIEW_HEADLINE =
  'Your precision health journey - what your body will do';

/**
 * Helper to compose the patient-journey subtitle (Omar Al-Rashidi line).
 * Built lazily so the patient store can swap fixtures in future phases.
 */
export function buildPatientJourneySubtitle(patient: PatientFixture): string {
  const sex = patient.sex === 'M' ? 'Male' : patient.sex === 'F' ? 'Female' : 'Other';
  // Brand-Patch v2: user-facing label uses clinical pathway name; internal
  // version id is preserved separately on the pathway resource per HC9.
  const pathwayLabel: Record<string, string> = {
    'cardiometabolic-pathway': 'Cardiometabolic Pathway',
    'hrt-pathway': 'HRT Pathway',
    'metabolic-syndrome-pathway': 'Metabolic Syndrome Pathway',
    'cvd-prevention-pathway': 'CVD Prevention Pathway',
  };
  const label = pathwayLabel[patient.pathway] ?? 'Care Pathway';
  return `${patient.name} · ${patient.age} · ${sex} · ${label} · Enrolled Apr 2026`;
}
